import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const stageElementVariants = cva(
	'relative inline-flex items-center transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2 rounded-l-md',
	{
		variants: {
			state: {
				default:
					'text-(--color-muted) hover:text-(--color-brand-light)',
				hover:    'text-(--color-brand-light)',
				selected: 'text-(--color-brand)',
			},
		},
		defaultVariants: { state: 'default' },
	}
)

const stageLabelVariants = cva('text-base leading-6 text-center whitespace-nowrap', {
	variants: {
		state: {
			default:  'text-(--color-muted-foreground)',
			hover:    'text-(--color-muted-foreground)',
			selected: 'text-(--color-background)',
		},
	},
	defaultVariants: { state: 'default' },
})

export interface StageElementProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof stageElementVariants> {
	/** If true, renders a <div> instead of a <button>. */
	asDiv?: boolean
}

const Tip = () => (
	<span
		aria-hidden
		className="self-start shrink-0"
		style={{
			width: 0,
			height: 0,
			borderTop: '18px solid transparent',
			borderBottom: '18px solid transparent',
			borderLeft: '14px solid currentColor',
		}}
	/>
)

const StageElement = React.forwardRef<HTMLButtonElement, StageElementProps>(
	({ className, state, children, asDiv, type = 'button', ...props }, ref) => {
		const inner = (
			<>
				<span className="flex h-9 items-center rounded-l-md bg-current pl-4 pr-2">
					<span className={stageLabelVariants({ state })}>{children}</span>
				</span>
				<Tip />
			</>
		)
		if (asDiv) {
			return (
				<div
					className={cn(stageElementVariants({ state, className }))}
					{...(props as React.HTMLAttributes<HTMLDivElement>)}
				>
					{inner}
				</div>
			)
		}
		return (
			<button
				ref={ref}
				type={type}
				className={cn(stageElementVariants({ state, className }), 'cursor-pointer')}
				{...props}
			>
				{inner}
			</button>
		)
	}
)

StageElement.displayName = 'StageElement'
export { StageElement, stageElementVariants }
